/**
 * @file withResolvers.ts
 * @description Polyfill for Promise.withResolvers()
 * Provides a standard way to create a Promise with externally accessible resolve/reject functions
 */

/**
 * Type definition for Promise.withResolvers return value
 */
export interface PromiseWithResolvers<T> {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
}

/**
 * Polyfill for Promise.withResolvers
 * Creates a new Promise and returns it along with its resolve and reject functions
 *
 * @template T - The type of value the promise will resolve to
 * @returns An object containing the promise and its resolve/reject functions
 *
 * @example
 * ```typescript
 * const { promise, resolve, reject } = Promise.withResolvers<string>();
 * setTimeout(() => resolve('Hello'), 1000);
 * const result = await promise; // 'Hello'
 * ```
 */
if (typeof Promise.withResolvers === 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (Promise as any).withResolvers = <T>(): PromiseWithResolvers<T> => {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;

    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });

    return { promise, resolve, reject };
  };
}

/**
 * Helper function to create a promise with resolvers
 * Can be used as a fallback in environments where the polyfill cannot modify Promise
 *
 * @template T - The type of value the promise will resolve to
 * @returns An object containing the promise and its resolve/reject functions
 */
export function createPromiseWithResolvers<T>(): PromiseWithResolvers<T> {
  let resolve!: (value: T | PromiseLike<T>) => void;
  let reject!: (reason?: unknown) => void;

  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  return { promise, resolve, reject };
}

// Augment the Promise interface with withResolvers
declare global {
  interface PromiseConstructor {
    withResolvers<T>(): PromiseWithResolvers<T>;
  }
}
